import React from "react";
import { Link } from "react-router-dom";
import { AvailableBadge, PhvBadge } from "@/components/Badges";
import { fmtPrice, waMessages, vehicleName } from "@/lib/whatsapp";
import { resolveImg } from "@/lib/apiClient";
import WhatsAppButton from "@/components/WhatsAppButton";
import { ArrowRight, Gauge } from "lucide-react";

export default function VehicleCard({ v, index = 0 }) {
  const priceNo = fmtPrice(v.weekly_price_without_insurance);
  const priceIns = fmtPrice(v.weekly_price_with_insurance);
  const rented = v.availability === "rented";
  const msg = rented ? waMessages.rented(v) : waMessages.vehicle(v);

  return (
    <article
      data-testid={`vehicle-card-${v.slug}`}
      className="group flex flex-col overflow-hidden border border-border bg-white transition-all duration-300 hover:border-zinc-900"
    >
      <Link to={`/cars/${v.slug}`} className="relative block overflow-hidden">
        <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-2">
          <AvailableBadge status={v.availability} />
        </div>
        {v.main_image ? (
          <img
            src={resolveImg(v.main_image)}
            alt={`${vehicleName(v)} — Manchester PHV rental`}
            loading={index < 3 ? "eager" : "lazy"}
            className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-56 w-full items-center justify-center bg-surface text-zinc-400">No image</div>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3">
          <PhvBadge status={v.phv_licence_status} />
        </div>
        <h3 className="font-heading text-xl font-bold tracking-tight text-ink">
          <Link to={`/cars/${v.slug}`} className="hover:underline">{vehicleName(v)}</Link>
        </h3>
        <p className="mt-1 text-sm text-zinc-500">
          {v.transmission} • {v.fuel_type} • {v.doors} Door
        </p>

        <div className="mt-4 flex items-end gap-4 border-t border-border pt-4">
          <div>
            <div className="font-heading text-3xl font-black leading-none text-ink" data-testid="card-price-no-insurance">
              {priceNo || "POA"}<span className="text-base font-semibold text-zinc-400">/wk</span>
            </div>
            <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500">Without Insurance</div>
          </div>
          {v.insurance_available && priceIns && (
            <div className="border-l border-border pl-4">
              <div className="font-heading text-2xl font-black leading-none text-zinc-700">
                {priceIns}<span className="text-sm font-semibold text-zinc-400">/wk</span>
              </div>
              <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500">With Insurance</div>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
          <Gauge className="h-4 w-4" /> {v.mileage?.toLocaleString()} miles
          {v.deposit ? <span>• Deposit £{Math.round(v.deposit)}</span> : null}
        </div>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link
            to={`/cars/${v.slug}`}
            data-testid={`view-details-${v.slug}`}
            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full border border-zinc-900 text-sm font-bold text-zinc-900 transition-colors hover:bg-zinc-900 hover:text-white"
          >
            View Details <ArrowRight className="h-4 w-4" />
          </Link>
          <WhatsAppButton
            size="md"
            className="flex-1"
            message={msg}
            testid={`card-whatsapp-${v.slug}`}
          >
            {rented ? "Ask About Similar" : "WhatsApp"}
          </WhatsAppButton>
        </div>
      </div>
    </article>
  );
}
